<template>
  <header id="mainHeader" class="sharkbet-header">
    <button class="sharkbet-logo" type="button" aria-label="Inicio" @click="changeView('lobby')">
      <img :src="skin.header_desktop.logo" alt="Sharkbet">
    </button>

    <nav class="sharkbet-nav" ref="navContainer" aria-label="Navegacion principal">
      <button v-for="item in visibleCategories" :key="item.url" type="button" class="sharkbet-nav__item"
        :class="{ 'is-active': isActive(item) }" @click="changeView(item.category)">
        {{ translate(item.name) }}
      </button>
      <div v-if="overflowItems.length" class="sharkbet-overflow" ref="overflowContainer">
        <button class="sharkbet-nav__item sharkbet-overflow__trigger" type="button" @click.stop="toggleOverflowMenu">
          <i class="fas fa-ellipsis-h"></i>
          <i class="fas fa-caret-down"></i>
        </button>
      </div>
    </nav>
    <div v-if="overflowItems.length && showOverflowMenu" class="sharkbet-overflow__menu" ref="overflowMenu"
      :style="{ left: overflowMenuLeft + 'px' }">
      <button v-for="item in overflowItems" :key="item.url" type="button" class="sharkbet-overflow__item"
        :class="{ 'is-active': isActive(item) }" @click="changeView(item.category); showOverflowMenu = false">
        {{ translate(item.name) }}
      </button>
    </div>

    <div class="sharkbet-actions">
      <form v-if="user.token === ''" class="sharkbet-login sharkbet-auth-container" @submit.prevent="submitLogin">
        <label class="visually-hidden" for="sharkbet-user">Nombre de usuario</label>
        <span class="sharkbet-input">
          <i class="fas fa-user-circle"></i>
          <input id="sharkbet-user" v-model.trim="username" type="text" autocomplete="username"
            :placeholder="translate('user') || 'Nombre de usuario'">
        </span>
        <label class="visually-hidden" for="sharkbet-password">Contraseña</label>
        <span class="sharkbet-input sharkbet-input--password">
          <i class="fas fa-lock"></i>
          <input id="sharkbet-password" v-model="password" :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password" :placeholder="translate('password') || 'Contraseña'">
          <button type="button" class="sharkbet-password-toggle"
            :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
            @click="showPassword = !showPassword">
            <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
        </span>
        <button class="sharkbet-btn sharkbet-btn--login" type="submit" :disabled="loggingIn">{{ loginLabel }}</button>
        <button v-if="skin.register" class="sharkbet-btn sharkbet-btn--register" type="button" @click="register">{{
          registerLabel }}</button>
        <button class="sharkbet-forgot" type="button" @click="openLoginDrawer">¿Olvidaste tu contraseña?</button>
        <span v-if="loginError" class="sharkbet-login__error">{{ loginError }}</span>
      </form>

      <div v-else ref="accountContainer" class="sharkbet-account sharkbet-auth-container">
        <div v-if="skin.header_desktop.balance" ref="balanceContainer" class="sharkbet-balance-wrap">
          <button type="button" class="sharkbet-balance" :aria-expanded="showBalance"
            @click.stop="showBalance = !showBalance; showAccountMenu = false">
            <span>Dinero:</span>
            <strong>$ {{ formattedBalance }}</strong>
            <i class="fas fa-caret-down sharkbet-balance__caret" aria-hidden="true"></i>
          </button>
          <div v-if="showBalance" class="sharkbet-balance__menu" @click.stop>
            <div class="sharkbet-balance__row">
              <span>Bonificacion de sport</span>
              <strong>$ {{ formattedSportsBonus }}</strong>
            </div>
            <div class="sharkbet-balance__row">
              <span>Bonificacion de Juego</span>
              <strong>$ {{ formattedGameBonus }}</strong>
            </div>
            <button type="button" class="sharkbet-balance__refresh" :disabled="refreshingProfile" @click="refreshProfile">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshingProfile }" aria-hidden="true"></i>
              Actualizar
            </button>
          </div>
        </div>
        <button type="button" class="sharkbet-user" @click="showAccountMenu = !showAccountMenu; showBalance = false">
          <i class="fas fa-user-circle"></i>
          <span>{{ user.user_data?.data?.user_name || '' }}</span>
          <i class="fas fa-caret-down sharkbet-user__caret" aria-hidden="true"></i>
        </button>
        <div v-if="showAccountMenu" class="sharkbet-account__menu">
          <account-menu :skin="skin" :language="language" :translation="translation" @dismiss="showAccountMenu = false"
            @logout="logout" />
        </div>
      </div>
      <language-selector :skin="skin" :language="language" />
    </div>

    <button id="login_drawer_btn" type="button" class="d-none" data-bs-toggle="offcanvas"
      data-bs-target="#loginDrawer"></button>
    <button id="register_btn" type="button" class="d-none" data-bs-toggle="modal" href="#registerModalToggle"></button>
    <register-modal :skin="skin" :language="language" :translation="translation" />
    <jm-account-modals v-if="skin.my_account && skin.my_account.myaccount_type === 'JM'" :language="language"
      :translation="translation" />
    <sharkbet-my-account :skin="skin" />

    <div class="offcanvas offcanvas-end" tabindex="-1" id="loginDrawer" aria-labelledby="loginDrawerLabel">
      <div class="offcanvas-header">
        <h5 class="offcanvas-title" id="loginDrawerLabel"></h5>
        <button ref="loginDrawerClose" type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas"
          aria-label="Close"></button>
      </div>
      <div class="offcanvas-body">
        <form v-if="user.token === ''" class="sharkbet-drawer-login" @submit.prevent="submitLogin">
          <div class="sharkbet-drawer-login__field">
            <label for="drawer-user">Nombre de usuario</label>
            <span class="sharkbet-drawer-login__input">
              <i class="fas fa-user-circle"></i>
              <input id="drawer-user" v-model.trim="username" type="text" autocomplete="username"
                :placeholder="translate('user') || 'Nombre de usuario'">
            </span>
          </div>
          <div class="sharkbet-drawer-login__field">
            <label for="drawer-password">Contraseña</label>
            <span class="sharkbet-drawer-login__input">
              <i class="fas fa-lock"></i>
              <input id="drawer-password" v-model="password" :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password" :placeholder="translate('password') || 'Contraseña'">
              <button type="button" class="sharkbet-password-toggle"
                :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click="showPassword = !showPassword">
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </span>
          </div>
          <button class="sharkbet-drawer-login__btn sharkbet-drawer-login__btn--login" type="submit"
            :disabled="loggingIn">{{ loginLabel }}</button>
          <button v-if="skin.register" class="sharkbet-drawer-login__btn sharkbet-drawer-login__btn--register"
            type="button" @click="register">{{ registerLabel }}</button>
          <a class="sharkbet-drawer-login__forgot" href="#" @click.prevent>¿Olvidaste tu contraseña?</a>
          <span v-if="loginError" class="sharkbet-drawer-login__error">{{ loginError }}</span>
        </form>
      </div>
    </div>
  </header>
</template>

<script>
import RegisterModal from './register.vue';
import LanguageSelector from '../../zeus/header/language.vue';
import AccountMenu from './account_menu.vue';
import JmAccountModals from './jm_account_modals.vue';
import SharkbetMyAccount from './my_account.vue';

export default {
  name: 'mainHeaderSharkbet',
  props: ['skin', 'language', 'translation'],
  components: { RegisterModal, LanguageSelector, AccountMenu, JmAccountModals, SharkbetMyAccount },
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      loginError: '',
      loggingIn: false,
      showBalance: false,
      refreshingProfile: false,
      showAccountMenu: false,
      visibleCategories: [],
      overflowItems: [],
      showOverflowMenu: false,
      overflowMenuLeft: 0,
      resizeObserver: null,
      profileInterval: null,
    };
  },
  computed: {
    user() {
      return this.$store.getters['getUserData'];
    },
    filteredCategories() {
      return (this.skin.categories || []).filter(category => category.active);
    },
    formattedBalance() {
      const amount = this.user.user_data?.data?.balance || 0;
      return this.formatAmount(amount);
    },
    formattedGameBonus() {
      return this.formatAmount(this.user.user_data?.data?.balance_bonus ?? this.user.user_data?.data?.bonus ?? 0);
    },
    formattedSportsBonus() {
      return this.formatAmount(this.user.user_data?.data?.balance_frozen ?? this.user.user_data?.data?.frozen ?? 0);
    },
    loginLabel() {
      return this.language === 'es' ? 'Ingresar' : this.language === 'pt' ? 'Entrar' : 'Login';
    },
    registerLabel() {
      return this.language === 'es' ? 'Registro' : this.language === 'pt' ? 'Registro' : 'Register';
    },
  },
  methods: {
    formatAmount(amount) {
      const decimals = this.skin.qBet_config?.decimalsQty || 0;
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(amount);
    },
    calculateOverflow() {
      const nav = this.$refs.navContainer;
      if (!nav) return;

      const GAP = 14;
      const OVERFLOW_BTN_WIDTH = 50;
      const navWidth = nav.offsetWidth;
      const allItems = this.filteredCategories;

      let totalWidth = 0;
      let visibleCount = allItems.length;

      for (let i = 0; i < allItems.length; i++) {
        const itemWidth = this.getItemWidth(allItems[i]);
        const needsOverflowBtn = i < allItems.length - 1;
        const reservedForBtn = needsOverflowBtn ? OVERFLOW_BTN_WIDTH + GAP : 0;

        if (totalWidth + itemWidth + reservedForBtn > navWidth) {
          visibleCount = i;
          break;
        }
        totalWidth += itemWidth + GAP;
      }

      this.visibleCategories = allItems.slice(0, visibleCount);
      this.overflowItems = allItems.slice(visibleCount);
    },
    getItemWidth(item) {
      const el = document.createElement('button');
      el.textContent = this.translate(item.name);
      el.className = 'sharkbet-nav__item';
      el.style.cssText = 'position:absolute;visibility:hidden;height:0;white-space:nowrap;font:700 16px Roboto,sans-serif;text-transform:uppercase;letter-spacing:0;';
      document.body.appendChild(el);
      const width = el.offsetWidth;
      document.body.removeChild(el);
      return width;
    },
    handleClickOutside(e) {
      const overflowTrigger = this.$refs.overflowContainer;
      const overflowMenu = this.$refs.overflowMenu;
      if (overflowTrigger && !overflowTrigger.contains(e.target) && overflowMenu && !overflowMenu.contains(e.target)) {
        this.showOverflowMenu = false;
      }

      const balanceContainer = this.$refs.balanceContainer;
      if (balanceContainer && !balanceContainer.contains(e.target)) {
        this.showBalance = false;
      }

      const accountContainer = this.$refs.accountContainer;
      if (accountContainer && !accountContainer.contains(e.target)) {
        this.showAccountMenu = false;
      }
    },
    toggleOverflowMenu() {
      this.showOverflowMenu = !this.showOverflowMenu;
      if (this.showOverflowMenu) {
        this.$nextTick(() => {
          const trigger = this.$refs.overflowContainer;
          if (trigger) {
            const rect = trigger.getBoundingClientRect();
            const headerRect = this.$el.getBoundingClientRect();
            this.overflowMenuLeft = rect.left - headerRect.left;
          }
        });
      }
    },
    isActive(item) {
      const current = this.$store.getters['getInfoRouter'];
      return current?.url === item.url;
    },
    changeView(view) {
      const currentView = this.$store.getters['getInfoRouter'];
      const reloadIframe = ['casino', 'casino_live'].includes(view) && currentView?.category === view;

      this.$store.dispatch('changeView', view);

      if (reloadIframe) {
        this.$store.dispatch('setRechargeIframe');
      }
    },
    async submitLogin() {
      this.loginError = '';
      if (!this.username || !this.password) {
        this.loginError = 'Completa usuario y contraseña';
        return;
      }
      this.loggingIn = true;
      const response = await this.$store.dispatch('login', { username: this.username, password: this.password });
      this.loggingIn = false;
      if (response === 'success') {
        this.$refs.loginDrawerClose?.click();
        this.username = '';
        this.password = '';
        this.$root.$emit('forceRender');
        return;
      }
      this.loginError = response;
    },
    register() {
      document.getElementById('register_btn').click();
    },
    openLoginDrawer() {
      document.getElementById('login_drawer_btn').click();
    },
    async refreshProfile() {
      if (!this.user.token || this.refreshingProfile) return;

      this.refreshingProfile = true;
      await this.$store.dispatch('getProfile', {
        token: this.user.token,
        site_id: this.user.site_id,
        site: this.user.site,
        prefix: this.user.prefix,
        playerId: this.user.user_data?.data?.user_id,
        source: 'main',
      });
      this.refreshingProfile = false;
    },
    logout() {
      this.$store.dispatch('logOut');
      this.$root.$emit('forceRender');
    },
    translate(text) {
      if (!text) return '';
      if (this.translation[text]) return this.translation[text][this.language];
      const normalized = text.replace(/ /g, '_');
      return this.translation[normalized]?.[this.language] || text;
    },
  },
  watch: {
    'skin.categories': {
      handler() {
        this.$nextTick(() => this.calculateOverflow());
      },
      deep: true,
    },
  },
  mounted() {
    this.$store.dispatch('setHeaderHeight', this.$el.offsetHeight + 16);
    this.$root.$on('openLogin', () => {
      this.openLoginDrawer();
    });
    this.$nextTick(() => this.calculateOverflow());
    this.resizeObserver = new ResizeObserver(() => this.calculateOverflow());
    if (this.$refs.navContainer) {
      this.resizeObserver.observe(this.$refs.navContainer);
    }
    document.addEventListener('click', this.handleClickOutside);
  },
  created() {
    this.profileInterval = setInterval(() => {
      if (!this.user.token || !this.user.user_data?.data) return;

      this.$store.dispatch('getProfile', {
        token: this.user.token,
        site_id: this.user.site_id,
        site: this.user.site,
        prefix: this.user.prefix,
        playerId: this.user.user_data.data.user_id,
        source: 'main',
      });
    }, 8000);
  },
  beforeDestroy() {
    this.$root.$off('openLogin');
    clearInterval(this.profileInterval);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
.sharkbet-header {
  align-items: center;
  background: #000;
  box-sizing: border-box;
  color: #fff;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  height: 72px;
  padding: 0 20px;
  position: relative;
  width: 100%;
  z-index: 20;
  border-bottom: 1px solid rgb(105, 112, 119);
}

.sharkbet-logo {
  align-items: center;
  background: none;
  border: 0;
  cursor: pointer;
  display: flex;
  grid-column: 1;
  height: 100%;
  justify-self: start;
  padding: 0;
  width: 116px;
}

.sharkbet-logo img {
  height: 100%;
  max-width: 116px;
  object-fit: contain;
  width: 100%;
}

.sharkbet-nav {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: center;
  grid-column: 2;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.sharkbet-nav__item {
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  font: 700 16px/64px 'Roboto', sans-serif;
  height: 64px;
  padding: 0;
  position: relative;
  text-transform: uppercase;
  white-space: nowrap;
}

.sharkbet-nav__item:hover,
.sharkbet-nav__item.is-active {
  color: #0092cd;
}

.sharkbet-nav__item.is-active::after {
  background: #0092cd;
  bottom: 14px;
  content: '';
  height: 2px;
  left: 0;
  position: absolute;
  right: 0;
}

.sharkbet-overflow {
  position: relative;
  flex-shrink: 0;
}

.sharkbet-overflow__trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 16px !important;
  letter-spacing: 1px;
}

.sharkbet-overflow__trigger .fa-caret-down {
  font-size: 12px;
}

.sharkbet-overflow__menu {
  position: absolute;
  top: calc(100%);
  background: #697077;
  border: 1px solid #454a55;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, .38);
  min-width: 180px;
  padding: 6px;
  z-index: 30;
}

.sharkbet-overflow__menu::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 20px;
  width: 12px;
  height: 12px;
  background: #697077;
  border-left: 1px solid #454a55;
  border-top: 1px solid #454a55;
  transform: rotate(45deg);
}

.sharkbet-overflow__item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #f3f4f6;
  font: 500 14px 'Roboto', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}

.sharkbet-account {
  background: #697077;
  padding-left: 7px;
  padding-right: 7px;
  border-radius: 20px;
}

.sharkbet-overflow__item:hover,
.sharkbet-overflow__item.is-active {
  background: #505661;
  color: #fff;
}

.sharkbet-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  grid-column: 3;
  justify-self: end;
}

.sharkbet-auth-container {
  align-items: center;
  display: flex;
  gap: 8px;
  position: relative;

}

.sharkbet-input {
  align-items: center;
  background: #fff;
  border-radius: 20px;
  color: #737b83;
  display: flex;
  height: 36px;
  padding: 0 11px;
  width: 156px;

}

.sharkbet-input i {
  font-size: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sharkbet-input--password {
  width: 178px;
}

.sharkbet-password-toggle {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  padding: 0;
}

.sharkbet-input--password .sharkbet-password-toggle {
  margin-left: auto;
}

.sharkbet-login input {
  background: transparent;
  border: 0;
  color: #262626;
  font: 14px 'Roboto', sans-serif;
  min-width: 0;
  outline: none;
  padding: 0 8px;
  width: 100%;
}

.sharkbet-login input::placeholder {
  color: #7c8590;
}

.sharkbet-btn {
  border: 0;
  border-radius: 20px;
  color: #fff;
  cursor: pointer;
  font: 500 14px 'Roboto', sans-serif;
  height: 36px;
  padding: 6px 16px;
  width: 300px;
  text-transform: none;
  white-space: nowrap;
}

.sharkbet-btn:disabled {
  cursor: wait;
  opacity: .7;
}

.sharkbet-btn--login {
  background: #697077;
  width: 84px;
}

.sharkbet-btn--register {
  background: #0092cd;
  width: 74px;
}

.sharkbet-forgot {
  background: transparent;
  border: 0;
  color: #0092cd;
  cursor: pointer;
  font: 12px 'Roboto', sans-serif;
  padding: 0;
  position: absolute;
  right: 185px;
  top: 39px;
  white-space: nowrap;
}

.sharkbet-login__error {
  bottom: calc(100% + 4px);
  color: #ff9ea6;
  font-size: 10px;
  left: 0;
  position: absolute;
  white-space: nowrap;
}

.sharkbet-account__menu {
  background: #697077;
  border-radius: 5px;
  display: grid;
  min-width: auto;
  padding: 8px;
  position: absolute;
  right: 0;
  top: calc(100% + 20px);
  z-index: 30;
}

.sharkbet-account__menu::before {
  border-bottom: 8px solid #697077;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  content: '';
  position: absolute;
  right: 16px;
  top: -8px;
}

.sharkbet-balance,
.sharkbet-user {
  align-items: center;
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  display: flex;
  font: 14px 'Roboto', sans-serif;
  gap: 6px;
  padding: 6px;
}

.sharkbet-balance {
  flex-direction: row;
  gap: 4px;
  text-align: right;
}

.sharkbet-balance span {
  color: #fff;
}

.sharkbet-balance-wrap {
  position: relative;
}

.sharkbet-balance__caret {
  font-size: 10px;
  margin-left: 2px;
}

.sharkbet-balance__menu {
  background: #697077;
  border-radius: 5px;
  color: #fff;
  min-width: 222px;
  padding: 8px;
  position: absolute;
  right: 0;
  top: calc(100% + 24px);
  z-index: 30;
}

.sharkbet-balance__menu::before {
  border-bottom: 8px solid #697077;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  content: '';
  position: absolute;
  right: 16px;
  top: -8px;
}

.sharkbet-balance__row {
  display: flex;
  font: 16px 'Roboto', sans-serif;
  line-height: 25px;
  justify-content: space-between;
  padding: 3px 0;
  white-space: nowrap;
}

.sharkbet-balance__row strong {
  font-weight: 400;
}

.sharkbet-balance__refresh {
  align-items: center;
  background: transparent;
  border: 1px solid #fff;
  border-radius: 22px;
  color: #fff;
  cursor: pointer;
  display: flex;
  font: 600 14px 'Roboto', sans-serif;
  gap: 12px;
  height: 37px;
  justify-content: center;
  margin-top: 10px;
  width: 100%;
}

.sharkbet-balance__refresh:disabled {
  cursor: wait;
  opacity: .75;
}

.sharkbet-user {

  border-radius: 20px;
  padding: 9px 12px;
}

.sharkbet-user__caret {
  font-size: 10px;
  margin-left: 2px;
}

.visually-hidden {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
}

@media (max-width: 1500px) {
  .sharkbet-logo {
    flex-basis: 220px;
  }

  .sharkbet-nav {
    gap: 13px;
  }

  .sharkbet-nav__item {
    font-size: 16px;
  }

  .sharkbet-input {
    width: 126px;
  }

  .sharkbet-input--password {
    width: 142px;
  }

  .sharkbet-btn--login {
    width: 84px;
  }

  .sharkbet-btn--register {
    width: 84px;
  }
}

@media (max-width: 899px) {
  .sharkbet-nav {
    display: none;
  }
}

#loginDrawer {
  background: #000020 !important;
  width: 282px !important;
  max-width: 90vw;
}

#loginDrawer .offcanvas-header {
  padding: 12px 16px 0;
  justify-content: flex-end;
}

#loginDrawer .offcanvas-body {
  padding: 0 16px 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.sharkbet-drawer-login {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sharkbet-drawer-login__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sharkbet-drawer-login__field label {
  color: #fff;
  font: 400 16px 'Roboto', sans-serif;
}

.sharkbet-drawer-login__field label::after {
  content: ' *';
  color: #fff;
}

.sharkbet-drawer-login__input {
  align-items: center;
  background: #fff;
  border-radius: 9999px;
  color: #333;
  display: flex;
  height: 40px;
  padding: 0 14px;
}

.sharkbet-drawer-login__input i {
  color: #999;
  font-size: 14px;
  flex-shrink: 0;
}

.sharkbet-drawer-login__input input {
  background: transparent;
  border: 0;
  color: #333;
  font: 14px 'Roboto', sans-serif;
  margin-left: 10px;
  outline: none;
  width: 100%;
}

.sharkbet-drawer-login__input input::placeholder {
  color: #999;
}

.sharkbet-drawer-login__input .sharkbet-password-toggle {
  cursor: pointer;
  margin-left: auto;
  color: #999;
}

.sharkbet-drawer-login__btn {
  border: 0;
  border-radius: 25px;
  cursor: pointer;
  font: 600 15px 'Roboto', sans-serif;
  height: 36.5px;
  width: 100%;
}

.sharkbet-drawer-login__btn--login {
  background: #697077;
  color: #fff;
}

.sharkbet-drawer-login__btn--register {
  background: #0092cd;
  color: #fff;
}

.sharkbet-drawer-login__forgot {
  color: rgba(255, 255, 255, .5);
  font: 400 13px 'Roboto', sans-serif;
  margin-top: 4px;
}

.sharkbet-drawer-login__forgot:hover {
  color: #0092cd;
}

.sharkbet-drawer-login__error {
  color: #ff9ea6;
  font-size: 12px;
  text-align: center;
}
</style>
